/**
 * SelecaoPCA — vínculo do DFD com um item do Plano de Contratações Anual.
 *
 * Aparece quando "Consta no PCA?" = Sim (Art. 12, VII da Lei 14.133/2021).
 * Réplica do bloco #pca-item-selector-container do new_dfd.html: filtro por
 * secretaria, seletor de item e preview do item escolhido.
 */

import {
  faCheckCircle,
  faExclamationCircle,
  faFilter,
  faInfoCircle,
  faLink,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { itensPCA } from '../../api/dfd'

export function SelecaoPCA({
  secretaria,
  itemId,
  aoMudar,
}: {
  secretaria: string
  itemId: string
  aoMudar: (campo: string, valor: string) => void
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['pca-itens'],
    queryFn: itensPCA,
    staleTime: 5 * 60 * 1000,
  })

  const itens = data?.itens ?? []
  const filtrados = useMemo(
    () => (secretaria ? itens.filter((i) => i.secretaria === secretaria) : itens),
    [itens, secretaria],
  )
  const selecionado = itens.find((i) => i.id === itemId)

  return (
    <div className="rounded-lg border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 p-6 dark:border-emerald-700 dark:from-emerald-900/20 dark:to-teal-900/20">
      <p className="mb-2 text-lg font-semibold text-emerald-800 dark:text-emerald-200">
        <FontAwesomeIcon icon={faLink} className="mr-2" />
        Vincular a Item do PCA
      </p>
      <p className="mb-4 text-sm text-emerald-700 dark:text-emerald-300">
        <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
        Selecione o item do PCA correspondente a esta contratação. Use o filtro
        por secretaria para facilitar.
        {data?.ano_exercicio ? ` PCA ${data.ano_exercicio}.` : ''}
      </p>

      {isLoading && (
        <p className="text-sm text-emerald-600 dark:text-emerald-300">
          <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
          Carregando itens do PCA…
        </p>
      )}

      {isError && (
        <p className="text-sm text-red-600 dark:text-red-400">
          <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
          Não foi possível carregar o PCA. Você pode seguir e vincular depois.
        </p>
      )}

      {!isLoading && !isError && itens.length === 0 && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
          Nenhum PCA cadastrado para sua prefeitura.
        </p>
      )}

      {itens.length > 0 && (
        <>
          <label
            htmlFor="pca_secretaria_filter"
            className="mb-1 block text-sm font-medium text-emerald-700 dark:text-emerald-300"
          >
            <FontAwesomeIcon icon={faFilter} className="mr-1" />
            Filtrar por Secretaria:
          </label>
          <select
            id="pca_secretaria_filter"
            name="pca_secretaria_filter"
            value={secretaria}
            onChange={(e) => {
              aoMudar('pca_secretaria_filter', e.target.value)
              // trocar de secretaria invalida o item escolhido antes
              if (selecionado && selecionado.secretaria !== e.target.value) {
                aoMudar('pca_item_id', '')
              }
            }}
            className="mb-3 w-full rounded-lg border border-emerald-200 bg-white px-4 py-2 text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-emerald-700 dark:bg-dark-card dark:text-slate-100"
          >
            <option value="">-- Todas as Secretarias --</option>
            {(data?.secretarias ?? []).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <label htmlFor="pca_item_id" className="sr-only">
            Item do PCA
          </label>
          <select
            id="pca_item_id"
            name="pca_item_id"
            value={itemId}
            onChange={(e) => aoMudar('pca_item_id', e.target.value)}
            className="w-full rounded-lg border-2 border-emerald-300 bg-white px-4 py-3 text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-emerald-700 dark:bg-dark-card dark:text-slate-100"
          >
            <option value="">-- Selecionar item do PCA --</option>
            {filtrados.map((item) => (
              <option key={item.id} value={item.id}>
                {[item.item, item.objeto].filter(Boolean).join(' — ')}
              </option>
            ))}
          </select>

          {selecionado && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-white p-4 dark:border-emerald-700 dark:bg-dark-card">
              <p className="mb-2 font-semibold text-emerald-800 dark:text-emerald-200">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                Item do PCA Selecionado
              </p>
              <dl className="grid grid-cols-1 gap-2 text-sm text-slate-700 sm:grid-cols-2 dark:text-slate-200">
                <div>
                  <dt className="inline font-semibold">Secretaria: </dt>
                  <dd className="inline">{selecionado.secretaria || '-'}</dd>
                </div>
                <div>
                  <dt className="inline font-semibold">Item: </dt>
                  <dd className="inline">{selecionado.item || '-'}</dd>
                </div>
                <div>
                  <dt className="inline font-semibold">Prioridade: </dt>
                  <dd className="inline">{selecionado.prioridade || 'Não definida'}</dd>
                </div>
                <div>
                  <dt className="inline font-semibold">Valor: </dt>
                  <dd className="inline">
                    {selecionado.valor_preliminar || 'R$ [Não informado]'}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="inline font-semibold">Objeto: </dt>
                  <dd className="inline">{selecionado.objeto || '-'}</dd>
                </div>
              </dl>
            </div>
          )}
        </>
      )}
    </div>
  )
}
